import { fruitAge2Date, julianCode2packingDate, packingDate2JulianCode } from "../../../../components/Helper/DatePicker";
import { calculateAbsoluteDifference, find_category } from "../utils";
import { packingDate2BakkerPackingCode } from "./bakker";
import { ISO6346_CONTAINER_NUMBER_CALLBACK, iso6346ContainerNumberOnChange } from "./containerNumber";
import { setBulkEditForMissionUSStage4Or5, setMissionUSSampleSizeAvocado, setSampleSizeMangoMissionUS } from "./mission_us";
import { setSampleSizeMehadrinISR } from "./mehadrin_isr";

export type SampleSizeVariables = {
    box_weight?: string | number;
    box_size?: number;
    fruit_size?: number;
    box_count?: number;
    number_of_boxes?: number;
};

export type SetValueFunction = (key: string, value: number) => void;

const setSampleSize = ({ box_weight, box_size, box_count = 1 }: SampleSizeVariables, setValue: SetValueFunction): void => {
    if (box_weight && box_size) {
        let sample_size = 0;
        if (box_weight === "4") {
            sample_size = box_size * box_count;
        } else if (box_weight === "9") {
            sample_size = box_size * 2.25 * box_count;
        } else if (box_weight === "10") {
            sample_size = box_size * 2.5 * box_count;
        } else if (box_weight === "20") {
            sample_size = box_size * 5 * box_count;
        }
        setValue("sample_size", Math.round(sample_size));
    }
};

const setMissionSampleSizeAvocado = ({ box_weight, box_size }: SampleSizeVariables, setValue: SetValueFunction): void => {
    if (box_weight && box_size) {
        let sample_size = 0;
        if (box_weight === "4") {
            sample_size = box_size * 4;
        } else if (box_weight === "10") {
            sample_size = box_size * 5;
        }
        setValue("sample_size", sample_size);
    }
};

const setMissionSampleSizeMango = ({ box_count, box_size }: SampleSizeVariables, setValue: SetValueFunction): void => {
    if (box_count && box_size) {
        const sample_size = box_count * box_size;
        setValue("sample_size", sample_size);
    }
};


const setSampleSizeAvocadoTAC = ({ box_weight, box_size }: SampleSizeVariables, setValue: SetValueFunction): void => {
    if (box_weight && box_size) {
        const multiplierByBoxWeight = {
            4: 3,
            9: 4.5,
            10: 5,
        };

        const sample_size = box_size * (multiplierByBoxWeight[box_weight] || 1);
        setValue("sample_size", Math.round(sample_size));
    }
};

export const onchange_callbacks = {
    set_size_category_for_size: ({ setValue, field, value }) => {
        setValue(field, value);
        // For as far as I know they are the same for every tenant
        const size_categories = [
            {
                gte: 26,
                lte: 32,
                label: "S",
                color: "black",
            },
            {
                gte: 18,
                lte: 24,
                label: "M",
                color: "black",
            },
            {
                gte: 8,
                lte: 16,
                label: "L",
                color: "black",
            },

        ];
        const cat = find_category(value, size_categories);
        if (cat) {
            setValue("size_category", cat.label);
        }
    },
    set_harvest_date_for_julian_code: ({ setValue, field, value }) => {
        setValue(field, value);
        setValue("harvest_date", value ? julianCode2packingDate(value) : undefined);
    },
    set_julian_code_for_harvest_date: ({ setValue, field, value }) => {
        setValue(field, value);
        setValue("julian_code", value ? packingDate2JulianCode(value) : undefined);
    },
    set_packing_code_for_packing_date_bakker: ({ setValue, field, value }) => {
        // packing_date -> packing_code (Bakker packing code), e.g. 2026-01-02 => AB1
        setValue(field, value);
        const code = value ? packingDate2BakkerPackingCode(value) : undefined;
        setValue("packing_code", code);
    },
    // Backwards compat alias (older configs may reference this key)
    set_date_code_for_packing_date_bakker: ({ setValue, field, value }) => {
        return onchange_callbacks.set_packing_code_for_packing_date_bakker({ setValue, field, value } as any);
    },
    set_harvest_date_to_for_min_fruit_age: ({ setValue, field, value }) => {
        setValue(field, value);
        setValue("harvest_date_to", fruitAge2Date(value));
    },
    set_harvest_date_from_for_max_fruit_age: ({ setValue, field, value }) => {
        setValue(field, value);
        setValue("harvest_date_from", fruitAge2Date(value));
    },
    set_sample_size_for_fruit_size: ({ setValue, field, value, object }) => {
        setValue(field, value);
        setSampleSize({
            box_weight: object.box_weight,
            box_size: value,
            box_count: object.box_count || 1
        }, setValue);
    },
    set_sample_size_for_box_weight: ({ setValue, field, value, object }) => {
        setValue(field, value);
        setSampleSize({
            box_weight: value,
            box_size: object.box_size,
            box_count: object.box_count || 1
        }, setValue);
    },
    set_mehadrin_nl_sample_size_for_box_weight_and_set_box_count: ({ setValue, field, value, object }) => {
        setValue(field, value);
        let box_count = 1;
        if (value === "4") {
            box_count = 3;
        } else if (value === "10") {
            box_count = 2;
        }
        setValue("box_count", box_count);

        setSampleSize({
            box_weight: value,
            box_size: object.box_size,
            box_count
        }, setValue);
    },
    set_sample_size_for_box_count: ({ setValue, field, value, object }) => {
        setValue(field, value);
        setSampleSize({
            box_weight: object.box_weight,
            box_size: object.box_size,
            box_count: value
        }, setValue);
    },
    set_mission_sample_size_for_fruit_size_avocado: ({ setValue, field, value, object }) => {
        setValue(field, value);
        setMissionSampleSizeAvocado({
            box_weight: object.box_weight,
            box_size: value,
        }, setValue);
    },
    set_mission_sample_size_for_box_weight_avocado: ({ setValue, field, value, object }) => {
        setValue(field, value);
        setMissionSampleSizeAvocado({
            box_weight: value,
            box_size: object.box_size,
        }, setValue);
    },
    set_mission_sample_size_for_fruit_size_mango: ({ setValue, field, value, object }) => {
        setValue(field, value);
        setMissionSampleSizeMango({
            box_count: object.box_count,
            box_size: value,
        }, setValue);
    },
    set_mission_sample_size_for_box_count_mango: ({ setValue, field, value, object }) => {
        setValue(field, value);
        setMissionSampleSizeMango({
            box_count: value,
            box_size: object.box_size,
        }, setValue);
    },
    set_box_weight_net_for_box_weight_gross: ({ setValue, field, value, object }) => {
        setValue(field, value);
        const box_weight_gross = value || 0;
        const box_weight_empty = object.box_weight_empty || 0;
        const box_weight_net = calculateAbsoluteDifference(box_weight_gross, box_weight_empty);

        setValue("box_weight_net", box_weight_net);
    },
    set_box_weight_net_for_box_weight_empty: ({ setValue, field, value, object }) => {
        setValue(field, value);
        const box_weight_empty = value || 0;
        const box_weight_gross = object.box_weight_gross || 0;
        const box_weight_net = calculateAbsoluteDifference(box_weight_gross, box_weight_empty);

        setValue("box_weight_net", box_weight_net);
    },
    set_ripening_cell_sample: ({ setValue, field, value }) => {
        setValue("ripening_sample", `${field} ${value}`);
    },
    set_sample_size_for_mango_mission_us: ({ setValue, field, value, object }) => {
        setValue(field, value);
        setSampleSizeMangoMissionUS({
            number_of_boxes: object.number_of_boxes,
            piece_count: object.piece_count,
        }, setValue);
    },
    set_sample_size_for_mehadrin_isr: ({ setValue, field, value, object }) => {
        setValue(field, value);
        setSampleSizeMehadrinISR({
            count: object.count,
            use_bulk_edit: object.use_bulk_edit,
        }, setValue);
    },
    set_mission_us_sample_size_for_avocado: ({ setValue, field, value, object }) => {
        setValue(field, value);
        setMissionUSSampleSizeAvocado({
            box_weight: object.box_weight,
            box_size: object.box_size,
        }, setValue);
    },
    set_sample_size_for_avocado_TAC: ({ setValue, field, value, object }) => {
        setValue(field, value);
        setSampleSizeAvocadoTAC({
            box_weight: object.box_weight,
            box_size: object.box_size,
            [field]: value,
        }, setValue);
    },
    set_bulk_edit_for_mission_us_stage_4_or_5: setBulkEditForMissionUSStage4Or5,
    [ISO6346_CONTAINER_NUMBER_CALLBACK]: iso6346ContainerNumberOnChange,
};
